/**
 * The arithmetic behind a drag-to-reorder list, with no DOM in it.
 *
 * The hook that drives the sidebar (`useDragReorder`) measures once at the
 * start of a drag and then only writes transforms; everything it needs to
 * decide — where the row would land, and how far every other row has to move
 * to make room — is computed here, so it can be tested without a browser.
 */

/** One item's resting geometry, in the scroll container's own coordinates. */
export interface ReorderMetrics {
  top: number;
  height: number;
}

export function moveItem<T>(items: readonly T[], from: number, to: number): T[] {
  const next = [...items];
  if (from === to || from < 0 || from >= next.length) return next;
  const [item] = next.splice(from, 1);
  next.splice(Math.max(0, Math.min(next.length, to)), 0, item!);
  return next;
}

/**
 * Where the dragged row wants to land, given how far it has been pulled.
 *
 * The list is measured with the dragged row lifted out of it, and the answer is
 * the slot whose top the row is closest to. Comparing tops rather than
 * overlaps keeps the gesture honest when the rows are different heights: a
 * project with a dozen tasks under it is not harder to place than a collapsed
 * one, and a drag that has not moved always resolves to where it started,
 * because the slot it came from is exactly where it is sitting.
 */
export function dropIndex(items: readonly ReorderMetrics[], from: number, delta: number, gap: number): number {
  const dragged = items[from];
  if (!dragged || items.length < 2) return from;
  const top = dragged.top + delta;
  let cursor = items[0]!.top;
  let best = 0;
  let bestDistance = Infinity;
  for (let slot = 0; slot < items.length; slot += 1) {
    const distance = Math.abs(top - cursor);
    if (distance < bestDistance) { bestDistance = distance; best = slot; }
    const other = items[slot < from ? slot : slot + 1];
    if (!other) break;
    cursor += other.height + gap;
  }
  return best;
}

/**
 * How far each row must move to sit where the drop would put it.
 *
 * Every offset is the difference between two absolute layout positions, so
 * rows of different heights — a collapsed project and one with a dozen tasks —
 * always close the gap exactly, and rounding never accumulates across a list.
 * The dragged row's own entry is its resting place, which is what the release
 * animates to; while the drag is live the pointer owns that row instead.
 */
export function reorderOffsets(items: readonly ReorderMetrics[], from: number, to: number, gap: number): number[] {
  const offsets = new Array<number>(items.length).fill(0);
  if (from === to || items.length === 0) return offsets;
  const order = moveItem(items.map((_, index) => index), from, to);
  let cursor = items[0]!.top;
  for (const index of order) {
    offsets[index] = cursor - items[index]!.top;
    cursor += items[index]!.height + gap;
  }
  return offsets;
}

/** Applies a persisted order to a live list: known ids first, in the stored
 * order, then anything the order has not seen. Mirrors the API's `orderRepos`,
 * so an optimistic sidebar and the next fetch agree. */
export function applyOrder<T extends { id: string }>(items: readonly T[], order: readonly string[]): T[] {
  if (order.length === 0) return [...items];
  const rank = new Map<string, number>();
  order.forEach((id, index) => { if (!rank.has(id)) rank.set(id, index); });
  const known: T[] = [];
  const rest: T[] = [];
  for (const item of items) (rank.has(item.id) ? known : rest).push(item);
  known.sort((left, right) => rank.get(left.id)! - rank.get(right.id)!);
  return [...known, ...rest];
}
